/**
 * API client service
 * Axios-based HTTP client for backend API
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

import { normalizeBatteryFraction } from '@/lib/utils';
import { CSRF_HEADER_NAME, UNSAFE_METHODS, readCsrfCookie, redirectToLogin } from './csrf';
import {
  SystemMetrics,
  BufferedSession,
  LogSession,
  SessionLogData,
  FleetDevice,
  FleetCommand,
  FleetEvent,
  FleetArchiveRequest,
  FleetIncident,
  SessionTelemetrySample,
  SessionCoverage,
  SessionTrimResult,
  ShareToken,
  MapDevice,
  DeviceSession,
  FleetSessionMeta,
  UploadTelemetry,
  CaptureSettingsBundle,
  TierProfileUpdate,
  DefaultUploadPolicy,
  EventUploadPolicy,
  EventUploadPolicyUpdate,
  V2UploadPolicyMode,
  V2UploadPolicyRow,
  V2UploadPolicyState,
  Principal,
  InvitePreview,
  PeopleListResponse,
} from '../types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      // P5b — cookie session auth: the browser attaches the
      // (HttpOnly) session cookie to every same-origin request on its
      // own once this is true; axios does NOT send cookies by default
      // even same-origin. Same-origin deployment only (Caddy
      // path-routes /api + / onto one host) — never paired with a
      // cross-origin baseURL.
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // P5b — attach the double-submit X-Tuvion-CSRF header on unsafe
    // methods, read from the (deliberately non-HttpOnly) tuv_csrf
    // cookie. See services/csrf.ts + backend services/auth/csrf.py.
    // No-op when the cookie isn't set (anonymous caller) — the header
    // is simply omitted, exactly like the retired bearer injection
    // used to no-op for anonymous surfaces. The AdminGate-bearer break
    // -glass path (backend `LEGACY_ADMIN_BEARER`, still on) is unaffected
    // either way — nothing in this SPA presents that credential anymore
    // (Phase 5b-E retired the last three components that did), so it's
    // purely a manual curl/ops fallback now, never something this
    // client's own requests carry.
    this.client.interceptors.request.use((config) => {
      const method = (config.method || 'get').toLowerCase();
      if (UNSAFE_METHODS.has(method)) {
        const token = readCsrfCookie();
        if (token) {
          config.headers = config.headers ?? {};
          config.headers[CSRF_HEADER_NAME] = token;
        }
      }
      return config;
    });

    // Response interceptor: redirect to /login on 401 (session
    // missing/expired). Most auth failures under the current authz
    // regime are 403/404, never 401 (require_authz's deny never
    // raises 401 — see services/auth/policy.py's denial-shape rule),
    // so this mainly catches /api/auth/logout-all's "no session at
    // all" 401 plus any future endpoint that intentionally uses it. A
    // 403 (logged in, wrong role) or 404 (private resource) does NOT
    // redirect here — the caller is authenticated, just not permitted
    // or not looking at something that exists; that's each page's own
    // error-surfacing job, not a global auth redirect.
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          redirectToLogin();
        }
        console.warn('API error:', error.response?.status, error.message);
        return Promise.reject(error);
      }
    );
  }

  // ── Auth (Phase 5b) ──────────────────────────────────────────────────

  /** Always succeeds (200) — resolves to `kind: 'anonymous'` rather
   *  than erroring when there's no session. See app/api/auth.py. */
  async whoami(): Promise<Principal> {
    const response = await this.client.get<Principal>('/auth/whoami');
    return response.data;
  }

  async loginStart(email: string): Promise<void> {
    await this.client.post('/auth/login/start', { email });
  }

  async loginVerify(email: string, code: string): Promise<Principal> {
    const response = await this.client.post<Principal>('/auth/login/verify', { email, code });
    return response.data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }

  async getInvitePreview(token: string): Promise<InvitePreview> {
    const response = await this.client.get<InvitePreview>(`/invite/${encodeURIComponent(token)}`);
    return response.data;
  }

  async acceptInvite(token: string): Promise<Principal> {
    const response = await this.client.post<Principal>(`/invite/${encodeURIComponent(token)}/accept`);
    return response.data;
  }

  // ── People / Team management (Phase 5b — app/api/people.py) ──────────

  async listTeamPeople(): Promise<PeopleListResponse> {
    const response = await this.client.get<PeopleListResponse>('/team/people');
    return response.data;
  }

  async inviteTeamPerson(email: string): Promise<{ invited: boolean; email: string }> {
    const response = await this.client.post('/team/invitations', { email });
    return response.data;
  }

  async revokeTeamPerson(userId: number): Promise<void> {
    await this.client.delete(`/team/people/${userId}`);
  }

  async listEventPeople(eventId: number): Promise<PeopleListResponse> {
    const response = await this.client.get<PeopleListResponse>(`/events/${eventId}/people`);
    return response.data;
  }

  async inviteEventPerson(eventId: number, email: string): Promise<{ invited: boolean; email: string }> {
    const response = await this.client.post(`/events/${eventId}/invitations`, {
      email,
      role: 'organizer',
    });
    return response.data;
  }

  async revokeEventPerson(eventId: number, userId: number): Promise<void> {
    await this.client.delete(`/events/${eventId}/people/${userId}`);
  }

  // System Metrics
  async getSystemMetrics(): Promise<SystemMetrics> {
    const response = await this.client.get<SystemMetrics>('/system/metrics');
    return response.data;
  }

  // Buffered HLS Streams
  async listBufferedSessions(): Promise<BufferedSession[]> {
    const response = await this.client.get<BufferedSession[]>('/streams/buffered');
    return response.data;
  }

  /**
   * Per-session lookup — fast path for PlaySessionPage. Returns null
   * when no segments are in S3 yet (pre-first-upload window of a fresh
   * recording). Avoids the O(sessions × cameras) full-walk that
   * `listBufferedSessions()` does just to .find() one session.
   */
  async getBufferedSession(sessionId: string): Promise<BufferedSession | null> {
    const response = await this.client.get<BufferedSession | null>(
      `/streams/buffered/${encodeURIComponent(sessionId)}`,
    );
    return response.data;
  }

  /**
   * Per-session video coverage — segment ranges per camera plus their union
   * in wall-clock UTC ms. Drives the public viewer's solid/dashed polyline.
   * Returns 200 with empty cameras/union for legacy sessions without a sidecar.
   */
  async getSessionCoverage(sessionId: string): Promise<SessionCoverage> {
    const response = await this.client.get<SessionCoverage>(
      `/streams/buffered/${encodeURIComponent(sessionId)}/coverage`,
    );
    return response.data;
  }

  // Session Logs
  async listLogSessions(): Promise<LogSession[]> {
    const response = await this.client.get<LogSession[]>('/logs/sessions');
    return response.data;
  }

  async getSessionLogs(device: string, sessionId: string): Promise<SessionLogData> {
    const response = await this.client.get<SessionLogData>(`/logs/sessions/${device}/${sessionId}`);
    return response.data;
  }

  async deleteLogSession(device: string, sessionId: string): Promise<void> {
    await this.client.delete(`/logs/sessions/${device}/${sessionId}`);
  }

  async deleteAllLogSessions(): Promise<void> {
    await this.client.delete('/logs/sessions');
  }

  // ── Fleet ────────────────────────────────────────────────────────────────

  /** Same mixed-unit battery problem as /map/devices (percent vs
   *  fraction depending on the reporting path) — the fleet surfaces
   *  showed "9300%" in a GREEN chip. Normalize every FleetDevice that
   *  enters the app to a 0–1 fraction. */
  private normalizeFleetDevice(d: FleetDevice): FleetDevice {
    if (d.battery?.level == null) return d;
    return {
      ...d,
      battery: { ...d.battery, level: normalizeBatteryFraction(d.battery.level) },
    };
  }

  async listFleetDevices(): Promise<FleetDevice[]> {
    const response = await this.client.get<FleetDevice[]>('/fleet/devices');
    return response.data.map((d) => this.normalizeFleetDevice(d));
  }

  /**
   * Organizer-scoped fleet: the devices enrolled in ONE event, in the
   * same `FleetDevice` shape as `listFleetDevices` (control-plane state +
   * gps/battery). The global `listFleetDevices` above is superadmin-only,
   * so an organizer uses this to see their event's Fleet — backed by
   * `GET /api/fleet/events/{id}/devices` (authz: organizer of the event /
   * superadmin).
   */
  async listEventFleetDevices(eventId: number): Promise<FleetDevice[]> {
    const response = await this.client.get<FleetDevice[]>(`/fleet/events/${eventId}/devices`);
    return response.data.map((d) => this.normalizeFleetDevice(d));
  }

  async getFleetDevice(deviceId: string): Promise<FleetDevice> {
    const response = await this.client.get<FleetDevice>(`/fleet/devices/${deviceId}`);
    return this.normalizeFleetDevice(response.data);
  }

  /** A6 — speed-test history, newest first (server-capped at 50). */
  async getDeviceSpeedTests(
    deviceId: string,
    options: { limit?: number } = {},
  ): Promise<
    Array<{
      mbps: number;
      bytes: number;
      duration_s: number;
      transport: string | null;
      created_at: string;
    }>
  > {
    const response = await this.client.get(
      `/fleet/devices/${deviceId}/speed-tests`,
      { params: { limit: options.limit ?? 50 } },
    );
    return response.data;
  }

  async enqueueFleetCommand(
    deviceId: string,
    kind: string,
    payload: Record<string, any> = {},
  ): Promise<FleetCommand> {
    const response = await this.client.post<FleetCommand>(
      `/fleet/devices/${deviceId}/commands`,
      { kind, payload },
    );
    return response.data;
  }

  async cancelFleetCommand(deviceId: string, commandUuid: string): Promise<FleetCommand> {
    const response = await this.client.delete<FleetCommand>(
      `/fleet/devices/${deviceId}/commands/${commandUuid}`,
    );
    return response.data;
  }

  async listFleetDeviceCommands(deviceId: string, limit = 100): Promise<FleetCommand[]> {
    const response = await this.client.get<FleetCommand[]>(
      `/fleet/devices/${deviceId}/commands`,
      { params: { limit } },
    );
    return response.data;
  }

  async listFleetCommands(inFlight = false, limit = 200): Promise<FleetCommand[]> {
    const response = await this.client.get<FleetCommand[]>('/fleet/commands', {
      params: { in_flight: inFlight, limit },
    });
    return response.data;
  }

  // ── Phase 2.B.2 — Session listing + metadata ─────────────────────────────

  /**
   * List sessions for a device — drives DeviceDetail's Recent Sessions
   * card. Backend unions `FleetSession` rows (V2 phone path) with S3
   * prefix-match (legacy / Radxa). Newest-first.
   */
  async listDeviceSessions(
    deviceId: string,
    /** Scope history to one event (rental/handover privacy); omit for
     *  the explicit all-history admin view. */
    eventId?: number | null,
  ): Promise<DeviceSession[]> {
    const response = await this.client.get<DeviceSession[]>(
      `/fleet/devices/${deviceId}/sessions`,
      eventId != null ? { params: { event_id: eventId } } : undefined,
    );
    return response.data;
  }

  /**
   * Full session metadata. Drives `/play/<sid>` page's `is_active` flag
   * (state === 'recording') and the header chip set.
   */
  async getSessionMetadata(sessionId: string): Promise<FleetSessionMeta> {
    const response = await this.client.get<FleetSessionMeta>(
      `/fleet/sessions/${encodeURIComponent(sessionId)}`,
    );
    return response.data;
  }

  // ── Phase 2.B.3 — Archive requests ───────────────────────────────────────

  async listDeviceArchiveRequests(
    deviceId: string,
    limit = 20,
  ): Promise<FleetArchiveRequest[]> {
    const response = await this.client.get<FleetArchiveRequest[]>(
      `/fleet/devices/${deviceId}/archive_requests`,
      { params: { limit } },
    );
    return response.data;
  }

  // ── Phase 2.B.5 — Incidents ──────────────────────────────────────────────

  async listDeviceIncidents(
    deviceId: string,
    limit = 50,
  ): Promise<FleetIncident[]> {
    const response = await this.client.get<FleetIncident[]>(
      `/fleet/devices/${deviceId}/incidents`,
      { params: { limit } },
    );
    return response.data;
  }

  async listIncidents(
    options: { state?: string; kind?: string; limit?: number } = {},
  ): Promise<FleetIncident[]> {
    const response = await this.client.get<FleetIncident[]>('/fleet/incidents', {
      params: {
        state_filter: options.state,
        kind_filter: options.kind,
        limit: options.limit ?? 200,
      },
    });
    return response.data;
  }

  /** M6 clips — request full-quality segments seg_start..seg_end
   *  (seg_end -1 = to the session's end); the backend enqueues
   *  request_archive and the phone re-uploads through tus. */
  async createArchiveRequest(
    deviceId: string,
    body: {
      session_id: string;
      camera_id: string;
      seg_start: number;
      seg_end: number;
      whole_session?: boolean;
      note?: string;
    },
  ): Promise<FleetArchiveRequest> {
    const response = await this.client.post<FleetArchiveRequest>(
      `/fleet/devices/${deviceId}/archive_requests`,
      body,
    );
    return response.data;
  }

  /** M6 — operator keep/discard decision; backend dispatches the
   *  incident_confirm command back to the phone. */
  async confirmIncident(
    incidentUuid: string,
    decision: 'keep' | 'discard',
    note?: string,
  ): Promise<FleetIncident> {
    const response = await this.client.post<FleetIncident>(
      `/fleet/incidents/${incidentUuid}/confirm`,
      { decision, note },
    );
    return response.data;
  }

  async listSessionIncidents(sessionId: string): Promise<FleetIncident[]> {
    const response = await this.client.get<FleetIncident[]>(
      `/fleet/sessions/${encodeURIComponent(sessionId)}/incidents`,
    );
    return response.data;
  }

  // ── Phase 2.F.3 — per-session telemetry reader ───────────────────────────

  async listSessionTelemetry(
    sessionId: string,
    options: { kind?: 'gps' | 'imu'; limit?: number } = {},
  ): Promise<SessionTelemetrySample[]> {
    const response = await this.client.get<SessionTelemetrySample[]>(
      `/fleet/sessions/${encodeURIComponent(sessionId)}/telemetry`,
      {
        params: { kind: options.kind, limit: options.limit ?? 5000 },
      },
    );
    return response.data;
  }

  // ── Share links ──────────────────────────────────────────────────────────

  async createShareLink(
    deviceId: string,
    sessionId: string,
    options: { expires_in_seconds: number; camera_id?: string | null; label?: string | null },
  ): Promise<ShareToken> {
    const response = await this.client.post<ShareToken>(
      `/fleet/devices/${deviceId}/sessions/${encodeURIComponent(sessionId)}/share`,
      options,
    );
    return response.data;
  }

  async listShareLinks(deviceId: string, sessionId: string): Promise<ShareToken[]> {
    const response = await this.client.get<ShareToken[]>(
      `/fleet/devices/${deviceId}/sessions/${encodeURIComponent(sessionId)}/share`,
    );
    return response.data;
  }

  async revokeShareLink(tokenId: number): Promise<ShareToken> {
    const response = await this.client.delete<ShareToken>(`/fleet/share/${tokenId}`);
    return response.data;
  }

  // ── Upload telemetry (DeviceDetail chart + log) ──────────────────────────

  async getDeviceUploadTelemetry(
    deviceId: string,
    options: { since_ms?: number; limit?: number } = {},
  ): Promise<UploadTelemetry> {
    const response = await this.client.get<UploadTelemetry>(
      `/fleet/devices/${deviceId}/upload-telemetry`,
      {
        params: {
          since_ms: options.since_ms,
          limit: options.limit ?? 720,
        },
      },
    );
    return response.data;
  }

  /** Saved per-session upload-pipeline history for the /play/<sid> page —
   *  the full work-start→drain window, not the device's 6 h ring. */
  async getSessionUploadTelemetry(
    sessionId: string,
    options: { since_ms?: number; limit?: number } = {},
  ): Promise<UploadTelemetry> {
    const response = await this.client.get<UploadTelemetry>(
      `/fleet/sessions/${sessionId}/upload-telemetry`,
      {
        params: {
          since_ms: options.since_ms,
          limit: options.limit ?? 5000,
        },
      },
    );
    return response.data;
  }

  /** Phase 5 B1 — operator "upload now" override. Enqueues a
   *  `set_allow_cellular` command so the session's device drains those
   *  chunks over a metered link despite the unmetered-only default gate.
   *  Whole session by default; `seq`/`tier` narrow the scope. */
  async uploadNow(
    sessionId: string,
    opts: { seq?: number; tier?: 'stream' | 'archive'; allow?: boolean } = {},
  ): Promise<{ session_id: string; device_id: string; command_uuid: string | null }> {
    const response = await this.client.post(
      `/fleet/sessions/${sessionId}/upload-now`,
      { seq: opts.seq, tier: opts.tier, allow: opts.allow ?? true },
    );
    return response.data;
  }

  /** Operator manual session link — mark `sessionId` as continuing
   *  `continuesSessionId` (the predecessor it resumes), joining them into
   *  one run regardless of the auto-stitch gap. Pass null to unlink. */
  async linkSession(
    deviceId: string,
    sessionId: string,
    continuesSessionId: string | null,
  ): Promise<{ session_id: string; device_id: string; continues_session_id: string | null }> {
    const response = await this.client.put(
      `/fleet/devices/${encodeURIComponent(deviceId)}/sessions/${encodeURIComponent(sessionId)}/link`,
      { continues_session_id: continuesSessionId },
    );
    return response.data;
  }

  // ── Phone map ─────────────────────────────────────────────

  async listMapDevices(): Promise<MapDevice[]> {
    const response = await this.client.get<MapDevice[]>('/fleet/map/devices');
    // Persisted battery levels arrive in mixed units (percent vs
    // fraction) depending on the reporting path — normalize here so
    // every consumer (fan viewer poll/replay seed, operator map) sees
    // a 0–1 fraction.
    return response.data.map((d) => ({
      ...d,
      battery_level: normalizeBatteryFraction(d.battery_level),
    }));
  }

  async rotateDeviceToken(deviceId: string, label?: string): Promise<{ device_id: string; token: string }> {
    const response = await this.client.post<{ device_id: string; token: string }>(
      `/fleet/devices/${deviceId}/tokens/rotate`,
      { label },
    );
    return response.data;
  }

  // ── Tier encoding profiles + audio toggle (admin Settings) ──────────────

  async getCaptureSettings(): Promise<CaptureSettingsBundle> {
    const response = await this.client.get<CaptureSettingsBundle>('/admin/tier-profiles');
    return response.data;
  }

  async updateTierProfile(
    tier: 'archive' | 'stream',
    body: TierProfileUpdate,
  ): Promise<CaptureSettingsBundle> {
    const response = await this.client.put<CaptureSettingsBundle>(
      `/admin/tier-profiles/${tier}`,
      body,
    );
    return response.data;
  }

  /**
   * Set the per-session upload-pause
   * flag for a phone's session. Updates the cloud-side FleetSession
   * mirror and enqueues a `set_session_upload_pause` WS command for
   * the phone, which updates its local SessionPauseStore.
   *
   * Use from DeviceDetail's session list when the operator wants to
   * stop uploads from a specific recording (e.g., known-bad test
   * session, or to free upload bandwidth for a different phone).
   */
  async setSessionUploadPause(
    deviceId: string,
    sessionId: string,
    paused: boolean,
  ): Promise<{ session_id: string; device_id: string; paused: boolean }> {
    const response = await this.client.put<{
      session_id: string;
      device_id: string;
      paused: boolean;
    }>(
      `/fleet/devices/${deviceId}/sessions/${sessionId}/upload-pause`,
      { paused },
    );
    return response.data;
  }

  /**
   * Phase 1 (transcode-rollout) — set or clear a device's encoder codec
   * preference. POST returns the updated FleetDevice so callers can
   * refresh state from the response. Pass `null` to clear the override.
   */
  /**
   * Admin rename — set or clear a device's display name. POST returns
   * the updated FleetDevice. Pass `null`/`''` to clear (UI falls back to
   * hostname). The name is dashboard-only; nothing is pushed to the
   * phone, and the WS heartbeat never overwrites it.
   */
  async renameDevice(deviceId: string, name: string | null): Promise<FleetDevice> {
    const response = await this.client.post<FleetDevice>(
      `/fleet/devices/${deviceId}/name`,
      { name },
    );
    return response.data;
  }

  /**
   * Permanently delete a recording session — S3 media + all DB rows
   * keyed by the session_id. Irreversible; the backend refuses while the
   * session is still recording (409).
   */
  async deleteSession(sessionId: string): Promise<{
    deleted: boolean;
    rows_deleted: number;
    s3_objects_deleted: number;
    s3_error: string | null;
  }> {
    const response = await this.client.delete(
      `/fleet/sessions/${encodeURIComponent(sessionId)}`,
    );
    return response.data;
  }

  /**
   * Trim a session to a kept segment range (inclusive; keep_seg_end -1 =
   * no tail trim). Range-DELETE at 6 s segment granularity — never a
   * media operation. `dryRun` previews the would-be freed segments/bytes
   * for the confirm dialog without mutating anything; the real call is
   * irreversible. `evictPhoneCopies` additionally releases the phone's
   * local copies of the trimmed ranges for disk eviction (M6 wiring).
   */
  async trimSession(
    sessionId: string,
    keepSegStart: number,
    keepSegEnd: number,
    opts: { dryRun?: boolean; evictPhoneCopies?: boolean } = {},
  ): Promise<SessionTrimResult> {
    const response = await this.client.post<SessionTrimResult>(
      `/fleet/sessions/${encodeURIComponent(sessionId)}/trim`,
      {
        keep_seg_start: keepSegStart,
        keep_seg_end: keepSegEnd,
        dry_run: !!opts.dryRun,
        evict_phone_copies: !!opts.evictPhoneCopies,
      },
    );
    return response.data;
  }

  /**
   * Download one camera's session (or a seg-range clip of it) as a
   * single zero-re-encode MP4 and hand it to the browser as a save-file.
   * Fetched as a blob (rather than a plain `<a href>`) because the
   * endpoint is session-cookie-authed (organizer/superadmin) — a bare
   * anchor navigation is a same-origin GET so the cookie WOULD ride
   * along automatically, but routing it through this client keeps the
   * 401-redirect interceptor in play if the session has since expired.
   */
  async downloadSessionClip(
    sessionId: string,
    cameraId: string,
    segStart?: number,
    segEnd?: number,
  ): Promise<void> {
    const params = new URLSearchParams();
    if (segStart != null) params.set('seg_start', String(segStart));
    if (segEnd != null) params.set('seg_end', String(segEnd));
    const qs = params.toString();
    const url =
      `/streams/buffered/${encodeURIComponent(sessionId)}/` +
      `${encodeURIComponent(cameraId)}/download.mp4${qs ? `?${qs}` : ''}`;
    const response = await this.client.get<Blob>(url, { responseType: 'blob' });
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const match = /filename="?([^";]+)"?/.exec(disposition);
    const range =
      segStart != null || segEnd != null
        ? `_seg${segStart ?? 'start'}-${segEnd ?? 'end'}`
        : '';
    const filename = match?.[1] ?? `${sessionId}_${cameraId}${range}.mp4`;
    const objectUrl = URL.createObjectURL(response.data);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      // Give the click a tick before revoking, or the download aborts.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    }
  }

  /** M3 — set or clear a device's quality override (sparse per-tier
   *  deltas that win over the global tier profiles). Persists on the
   *  device row AND enqueues a merged set_capture_settings for the
   *  phone; applies on the next recording session. */
  async setDeviceCaptureOverride(
    deviceId: string,
    override: {
      tiers: Record<string, { width?: number; height?: number; bitrate_bps?: number; fps?: number }>;
    } | null,
  ): Promise<FleetDevice> {
    const response = await this.client.post<FleetDevice>(
      `/fleet/devices/${deviceId}/capture-override`,
      { override },
    );
    return response.data;
  }

  /**
   * T4 — set or clear a device's video-stabilization override.
   * Ladder: override > fleet default > 'auto'. The backend pushes the
   * RESOLVED mode to the phone via `settings.update`; it applies at
   * the phone's NEXT capture start (never mid-recording). Pass null
   * to follow the fleet default.
   */
  async setDeviceStabilizationOverride(
    deviceId: string,
    mode: 'off' | 'auto' | 'eis' | 'ois' | 'both' | null,
  ): Promise<FleetDevice> {
    const response = await this.client.post<FleetDevice>(
      `/fleet/devices/${deviceId}/stabilization-override`,
      { mode },
    );
    return response.data;
  }

  async setDeviceCodecPreference(
    deviceId: string,
    preference: Array<'video/avc' | 'video/hevc'> | null,
  ): Promise<FleetDevice> {
    const response = await this.client.post<FleetDevice>(
      `/fleet/devices/${deviceId}/codec-preference`,
      { preference },
    );
    return response.data;
  }

  /**
   * Camera-selection rollout — set or clear a device's camera selector
   * ("back" | "front" | "external"). POST returns the updated
   * FleetDevice. Pass `null` to clear (revert to back camera). The
   * backend persists the value and enqueues a `set_camera_selector`
   * command in the same request.
   */
  async setDeviceCameraSelector(
    deviceId: string,
    selector: 'back' | 'front' | 'external' | null,
  ): Promise<FleetDevice> {
    const response = await this.client.post<FleetDevice>(
      `/fleet/devices/${deviceId}/camera-selector`,
      { selector },
    );
    return response.data;
  }

  /**
   * Orientation-correction — set or clear a device's DEFAULT display
   * rotation (deg, 0|90|180|270). Display-only: the public viewer applies
   * a CSS transform; the phone is NOT told (no WS command). Pass null to
   * clear. POST returns the updated FleetDevice.
   */
  async setDeviceRotation(
    deviceId: string,
    rotation: 0 | 90 | 180 | 270 | null,
  ): Promise<FleetDevice> {
    const response = await this.client.post<FleetDevice>(
      `/fleet/devices/${deviceId}/video-rotation`,
      { video_rotation: rotation },
    );
    return response.data;
  }

  /**
   * Orientation-correction — set or clear a SESSION display-rotation
   * override (deg). Overrides the device default for that one session;
   * null falls back to the device default. PUT, operator-gated.
   */
  async setSessionRotation(
    sessionId: string,
    rotation: 0 | 90 | 180 | 270 | null,
  ): Promise<{ session_id: string; video_rotation: number | null }> {
    const response = await this.client.put<{
      session_id: string;
      video_rotation: number | null;
    }>(`/fleet/sessions/${sessionId}/video-rotation`, { video_rotation: rotation });
    return response.data;
  }

  async updateAudioConfig(audioEnabled: boolean): Promise<CaptureSettingsBundle> {
    const response = await this.client.put<CaptureSettingsBundle>('/admin/capture-config', {
      audio_enabled: audioEnabled,
    });
    return response.data;
  }

  /** T4 — fleet-wide stabilization default (null = auto). Pushes to
   *  every phone WITHOUT a per-device override. */
  async updateStabilizationDefault(
    mode: 'off' | 'auto' | 'eis' | 'ois' | 'both' | null,
  ): Promise<CaptureSettingsBundle> {
    const response = await this.client.put<CaptureSettingsBundle>('/admin/stabilization-default', {
      mode,
    });
    return response.data;
  }

  async updateDefaultUploadPolicy(body: DefaultUploadPolicy): Promise<CaptureSettingsBundle> {
    const response = await this.client.put<CaptureSettingsBundle>(
      '/admin/default-upload-policy',
      body,
    );
    return response.data;
  }

  // ── Per-event upload policy (admin EventTab right column) ────────

  async getEventUploadPolicy(eventId: number): Promise<EventUploadPolicy> {
    const response = await this.client.get<EventUploadPolicy>(
      `/fleet/events/${eventId}/upload-policy`,
    );
    return response.data;
  }

  async updateEventUploadPolicy(
    eventId: number,
    body: EventUploadPolicyUpdate,
  ): Promise<EventUploadPolicy> {
    const response = await this.client.put<EventUploadPolicy>(
      `/fleet/events/${eventId}/upload-policy`,
      body,
    );
    return response.data;
  }

  // ── A5 — event-scoped v2 upload authority (S9 policy card) ────────

  async getV2UploadPolicyState(eventId: number): Promise<V2UploadPolicyState> {
    const response = await this.client.get<V2UploadPolicyState>(
      `/admin/events/${eventId}/v2-upload-policy`,
    );
    return response.data;
  }

  async setV2UploadPolicy(
    eventId: number,
    body: { mode: V2UploadPolicyMode; device_id?: string | null; expected_revision?: number },
  ): Promise<V2UploadPolicyRow> {
    const response = await this.client.put<V2UploadPolicyRow>(
      `/admin/events/${eventId}/v2-upload-policy`,
      body,
    );
    return response.data;
  }

  /**
   * Subscribe to fleet events via SSE. Returns a function that closes the
   * underlying EventSource when called.
   */
  subscribeFleetEvents(onEvent: (event: FleetEvent) => void, onError?: (err: Event) => void): () => void {
    const source = new EventSource('/api/events/stream');
    source.onmessage = (msg) => {
      try {
        const parsed = JSON.parse(msg.data) as FleetEvent;
        onEvent(parsed);
      } catch (e) {
        // Ignore malformed lines (heartbeat comments arrive as ":heartbeat" and don't fire onmessage anyway).
      }
    };
    if (onError) {
      source.onerror = onError;
    }
    return () => source.close();
  }
}

export const api = new ApiClient();
export default api;
